<template>
  <div class="user-area">
    <template v-if="isLoggedIn">
      <!-- 桌面：悬停弹出 -->
      <el-popover
        v-if="!isMobile"
        v-model="popoverVisible"
        placement="bottom-end"
        width="340"
        trigger="hover"
        popper-class="user-popover"
        :open-delay="120"
        :close-delay="80"
        :visible-arrow="false"
        transition="el-zoom-in-top"
      >
        <user-menu-panel v-bind="panelProps" @navigate="onNavigate" @checkin="handleCheckin" @logout="handleLogout" />
        <div
          slot="reference"
          class="user-trigger"
          :class="{ 'is-member': isMember, open: popoverVisible }"
          @click="goProfile"
        >
          <default-user-avatar size="sm" :member="isMember" :src="user.avatar" class="trigger-avatar" />
          <span class="username-text">
            {{ user.username }}
            <i v-if="isMember" class="el-icon-medal member-icon" :title="$t('user.member')" />
          </span>
          <span
            v-if="pointsEnabled"
            class="points-pill"
            @click.stop="goProfileTab('points')"
          >
            <i class="el-icon-coin" />{{ userPoints }}
          </span>
          <i class="el-icon-arrow-down trigger-arrow" />
        </div>
      </el-popover>

      <!-- 手机：头像按钮 + 底部抽屉 -->
      <template v-else>
        <button
          type="button"
          class="user-trigger is-compact"
          :class="{ 'is-member': isMember, open: sheetOpen }"
          :aria-label="$t('user.menu')"
          @click="sheetOpen = true"
        >
          <default-user-avatar size="sm" :member="isMember" :src="user.avatar" class="trigger-avatar" />
        </button>

        <el-drawer
          :visible.sync="sheetOpen"
          direction="btt"
          size="88%"
          :with-header="false"
          :append-to-body="true"
          custom-class="user-menu-sheet"
          :modal-append-to-body="true"
        >
          <user-menu-panel
            sheet
            v-bind="panelProps"
            @navigate="onNavigate"
            @checkin="handleCheckin"
            @logout="handleLogout"
          />
        </el-drawer>
      </template>
    </template>

    <button
      v-else
      type="button"
      class="guest-login-btn"
      :title="$t('auth.login')"
      :aria-label="$t('auth.login')"
      @click="openLogin"
    >
      <guest-login-icon />
    </button>
  </div>
</template>

<script>
import { auth } from '@/store/auth'
import { historyStore } from '@/store/history'
import DefaultUserAvatar from '@/components/DefaultUserAvatar.vue'
import GuestLoginIcon from '@/components/GuestLoginIcon.vue'
import UserMenuPanel from '@/components/UserMenuPanel.vue'
import api from '@/api/client'

const DEFAULT_POINTS_SETTINGS = {
  enabled: true,
  pointsName: '积分',
  checkinEnabled: true,
  checkinPoints: 10
}

const MOBILE_MQ = '(max-width: 768px)'

export default {
  name: 'UserArea',
  components: { DefaultUserAvatar, GuestLoginIcon, UserMenuPanel },
  data() {
    return {
      tick: 0,
      checkingIn: false,
      pointsSettings: { ...DEFAULT_POINTS_SETTINGS },
      isMobile: false,
      popoverVisible: false,
      sheetOpen: false
    }
  },
  computed: {
    user() {
      // eslint-disable-next-line no-unused-expressions
      this.tick
      return auth.user
    },
    isLoggedIn() {
      // eslint-disable-next-line no-unused-expressions
      this.tick
      return auth.isLoggedIn
    },
    isMember() {
      // eslint-disable-next-line no-unused-expressions
      this.tick
      return auth.isMember
    },
    recentHistory() {
      // eslint-disable-next-line no-unused-expressions
      this.tick
      return historyStore.getHistory(5)
    },
    historyCount() {
      // eslint-disable-next-line no-unused-expressions
      this.tick
      return historyStore.getCount()
    },
    pointsEnabled() {
      return this.pointsSettings.enabled !== false
    },
    pointsName() {
      return this.pointsSettings.pointsName || '积分'
    },
    userPoints() {
      // eslint-disable-next-line no-unused-expressions
      this.tick
      return auth.points
    },
    checkinEnabled() {
      return this.pointsEnabled && this.pointsSettings.checkinEnabled !== false
    },
    checkin() {
      // eslint-disable-next-line no-unused-expressions
      this.tick
      return auth.checkin || {}
    },
    checkedInToday() {
      return !!this.checkin.checkedInToday
    },
    checkinStreak() {
      return this.checkin.streak || 0
    },
    todayReward() {
      if (this.checkin.todayReward) return this.checkin.todayReward
      return this.pointsSettings.checkinPoints || 10
    },
    panelProps() {
      return {
        user: this.user || {},
        isMember: this.isMember,
        pointsEnabled: this.pointsEnabled,
        pointsName: this.pointsName,
        userPoints: this.userPoints,
        checkinEnabled: this.checkinEnabled,
        checkedInToday: this.checkedInToday,
        checkinStreak: this.checkinStreak,
        todayReward: this.todayReward,
        checkingIn: this.checkingIn,
        recentHistory: this.recentHistory,
        historyCount: this.historyCount
      }
    }
  },
  watch: {
    '$route'() {
      this.closeMenus()
    }
  },
  mounted() {
    this._refresh = () => { this.tick++ }
    window.addEventListener('auth-change', this._refresh)
    window.addEventListener('history-change', this._refresh)
    this._mq = window.matchMedia(MOBILE_MQ)
    this.isMobile = this._mq.matches
    this._onMq = (e) => {
      this.isMobile = e.matches
      this.closeMenus()
    }
    if (this._mq.addEventListener) this._mq.addEventListener('change', this._onMq)
    else this._mq.addListener(this._onMq)
    this.loadPointsSettings()
    if (auth.isLoggedIn) {
      auth.refreshUser({ soft: true })
    }
  },
  beforeDestroy() {
    window.removeEventListener('auth-change', this._refresh)
    window.removeEventListener('history-change', this._refresh)
    if (this._mq) {
      if (this._mq.removeEventListener) this._mq.removeEventListener('change', this._onMq)
      else this._mq.removeListener(this._onMq)
    }
  },
  methods: {
    closeMenus() {
      this.popoverVisible = false
      this.sheetOpen = false
    },
    async loadPointsSettings() {
      try {
        const res = await api.getSiteConfig()
        if (res.data.pointsSettings) {
          this.pointsSettings = { ...DEFAULT_POINTS_SETTINGS, ...res.data.pointsSettings }
        }
      } catch {
        // 忽略
      }
    },
    openLogin() {
      auth.openAuthDialog('login')
    },
    onNavigate(payload) {
      this.closeMenus()
      if (!payload || !payload.type) return
      if (payload.type === 'profile') this.goProfile()
      else if (payload.type === 'profile-tab') this.goProfileTab(payload.tab)
      else if (payload.type === 'sponsor') this.goSponsor()
      else if (payload.type === 'article') this.goArticle(payload.id)
    },
    goProfile() {
      const path = this.localePath('/profile')
      if (this.$route.path === path) return
      this.$router.push(path)
    },
    goProfileTab(tab) {
      const path = this.localePath('/profile')
      if (this.stripLocalePath(this.$route.path) === '/profile') {
        this.$router.replace({ path, query: { tab } })
        return
      }
      this.$router.push({ path, query: { tab } })
    },
    goSponsor() {
      const path = this.localePath('/sponsor')
      if (this.$route.path === path) return
      this.$router.push(path)
    },
    goArticle(id) {
      const path = this.localePath(`/article/${id}`)
      if (this.$route.path === path) return
      this.$router.push(path)
    },
    async handleCheckin() {
      if (this.checkedInToday || this.checkingIn) return
      this.checkingIn = true
      const result = await auth.checkin()
      this.checkingIn = false
      if (result.success) {
        if (result.alreadyCheckedIn) this.$message.info(result.message)
        else this.$message.success(result.message)
      } else {
        this.$message.warning(result.message)
      }
    },
    async handleLogout() {
      this.closeMenus()
      await auth.logout()
      this.$message.success(this.$t('auth.loggedOut'))
      if (this.stripLocalePath(this.$route.path) === '/profile') {
        this.$router.push(this.localePath('/'))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.user-area {
  flex-shrink: 0;
}

.guest-login-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 6px;
  border: 1px solid transparent;
  border-radius: 50%;
  background: transparent;
  color: $text-primary;
  cursor: pointer;
  transition: all 0.22s ease;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: $primary-bg;
    border-color: $primary-border;
    transform: translateY(-1px);
    box-shadow: $shadow-sm;

    ::v-deep path.accent {
      fill: $primary-color;
    }
  }

  &:active {
    transform: translateY(0);
  }
}

.user-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 4px;
  border-radius: 999px;
  cursor: pointer;
  border: 1px solid transparent;
  background: transparent;
  transition: all 0.25s ease;
  -webkit-tap-highlight-color: transparent;

  &:hover,
  &.open {
    background: $primary-bg;
    border-color: $primary-border;

    .trigger-arrow {
      transform: rotate(180deg);
      color: $primary-color;
    }
  }

  &:active {
    transform: scale(0.98);
  }

  &.is-member .trigger-avatar {
    box-shadow: 0 2px 8px rgba(217, 119, 6, 0.35);
  }

  &.is-compact {
    width: 38px;
    height: 38px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;

    &:active {
      background: $primary-bg;
      border-color: $primary-border;
    }
  }
}

.trigger-avatar {
  flex-shrink: 0;
}

.username-text {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;
  color: $text-primary;
  max-width: 88px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.member-icon {
  color: #d97706;
  font-size: 14px;
  flex-shrink: 0;
}

.trigger-arrow {
  font-size: 12px;
  color: $text-muted;
  transition: transform 0.25s ease, color 0.25s;
}

.points-pill {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 999px;
  background: linear-gradient(135deg, #ede9fe, #ddd6fe);
  color: #7c3aed;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  cursor: pointer;

  i { font-size: 11px; }
}
</style>

<style lang="scss">
.user-popover {
  padding: 12px !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 14px !important;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.12), 0 4px 12px rgba(37, 99, 235, 0.08) !important;
  overflow: hidden;
  max-width: calc(100vw - 24px) !important;
}

.user-menu-sheet {
  border-radius: 20px 20px 0 0 !important;
  overflow: hidden !important;

  .el-drawer__body {
    padding: 0;
    height: 100%;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}
</style>
